/**
 * CoverRenderer3D.js
 *
 * Renders a closed magazine cover using Three.js.
 * Uses a BoxGeometry to simulate the thickness of a perfect-bound magazine,
 * with the cover image mapped to the top face and paper edges on the sides.
 */

import * as THREE from 'three';

const DEFAULTS = {
  width: 1000,
  height: 1000,
  thickness: 0.15,      // Thickness of the page block
  cameraTiltX: 35,      // Camera looking down (degrees)
  cameraRotateY: -20,   // Isometric spin (degrees)
  cameraRotateZ: 0,     // Spin flat on the table
  lightIntensity: 1.5,
  ambientIntensity: 0.6,
  shadowStrength: 0.25, // Ground contact shadow opacity (0 = none)
  // Image framing within the cover
  aspectRatio: null,    // null = derive from source image; else paper h/w ratio
  zoom: 1,              // >1 zooms into the image (crops)
  panX: 0,              // -1..1 horizontal reposition within crop slack
  panY: 0,              // -1..1 vertical reposition within crop slack
  roughness: 0.3,       // 0 = glossy, 1 = matte paper finish
};

export class CoverRenderer3D {
  constructor(defaults = {}) {
    this.defaults = { ...DEFAULTS, ...defaults };

    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: true });
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
  }

  /**
   * Render the cover using WebGL
   * @param {HTMLCanvasElement} coverCanvas 
   * @param {object} options 
   * @returns {HTMLCanvasElement}
   */
  render(coverCanvas, options = {}) {
    const opts = { ...this.defaults, ...options };
    
    this.renderer.setSize(opts.width, opts.height);
    this.renderer.setClearColor(0x000000, 0);

    const scene = new THREE.Scene();

    // 1. Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 1.0);
    scene.add(ambientLight);

    const dirLight = new THREE.DirectionalLight(0xffffff, 2.5);
    dirLight.position.set(-5, 10, 5);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.width = 2048;
    dirLight.shadow.mapSize.height = 2048;
    dirLight.shadow.camera.near = 0.5;
    dirLight.shadow.camera.far = 50;
    dirLight.shadow.camera.left = -5;
    dirLight.shadow.camera.right = 5;
    dirLight.shadow.camera.top = 5;
    dirLight.shadow.camera.bottom = -5;
    dirLight.shadow.bias = -0.001;
    scene.add(dirLight);

    const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);
    fillLight.position.set(5, 5, -5);
    scene.add(fillLight);

    // 2. Floor
    const floorGeo = new THREE.PlaneGeometry(30, 30);
    const floorMat = new THREE.ShadowMaterial({ opacity: opts.shadowStrength });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    scene.add(floor);

    // 3. Materials
    const imageAspect = coverCanvas.height / coverCanvas.width;
    const targetAspect = (opts.aspectRatio && opts.aspectRatio > 0) ? opts.aspectRatio : imageAspect;

    const coverTex = new THREE.CanvasTexture(coverCanvas);
    coverTex.colorSpace = THREE.SRGBColorSpace;
    coverTex.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
    this._applyTextureTransform(coverTex, imageAspect, targetAspect, opts);

    const coverMat = new THREE.MeshStandardMaterial({ 
      map: coverTex, 
      roughness: opts.roughness, 
      metalness: 0.05 
    });

    const paperMat = new THREE.MeshStandardMaterial({ 
      color: 0xf4f4f4, // Off-white paper edges
      roughness: 0.8,  // Matte paper block
      metalness: 0.0 
    });

    // BoxGeometry material array:
    // [right, left, top, bottom, front, back]
    const materials = [
      paperMat, // right (page edges)
      paperMat, // left (spine)
      coverMat, // top (cover)
      paperMat, // bottom (back cover)
      paperMat, // front (bottom page edges)
      paperMat  // back (top page edges)
    ];

    // 4. Geometry
    const pageW = 3;
    const pageH = pageW * targetAspect;

    // Box dimensions: Width (X), Thickness (Y), Height (Z)
    const boxGeo = new THREE.BoxGeometry(pageW, opts.thickness, pageH);
    
    const mesh = new THREE.Mesh(boxGeo, materials);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    
    // Position it so the bottom of the magazine rests on the floor (Y=0)
    mesh.position.y = opts.thickness / 2;
    
    const magGroup = new THREE.Group();
    magGroup.add(mesh);
    magGroup.rotation.y = THREE.MathUtils.degToRad(opts.cameraRotateZ);
    scene.add(magGroup);

    // 5. Camera
    const camera = new THREE.PerspectiveCamera(30, opts.width / opts.height, 0.1, 100);
    
    // Auto-frame camera based on bounding box
    const box = new THREE.Box3().setFromObject(magGroup);
    const size = new THREE.Vector3();
    box.getSize(size);
    const center = new THREE.Vector3();
    box.getCenter(center);

    const maxDim = Math.max(size.x, size.z); // Y is mostly flat
    const paddedDim = maxDim * 1.15; // 15% padding
    
    const fov = camera.fov * (Math.PI / 180);
    const radius = Math.abs(paddedDim / (2 * Math.tan(fov / 2)));
    
    const tiltRad = THREE.MathUtils.degToRad(opts.cameraTiltX);
    const rotRad = THREE.MathUtils.degToRad(opts.cameraRotateY);
    
    camera.position.y = radius * Math.cos(tiltRad);
    camera.position.z = radius * Math.sin(tiltRad) * Math.cos(rotRad);
    camera.position.x = radius * Math.sin(tiltRad) * Math.sin(rotRad);
    
    camera.lookAt(center);

    // Render
    this.renderer.render(scene, camera);

    const outCanvas = document.createElement('canvas');
    outCanvas.width = opts.width;
    outCanvas.height = opts.height;
    const outCtx = outCanvas.getContext('2d');
    outCtx.drawImage(this.renderer.domElement, 0, 0);
    
    // Cleanup
    coverTex.dispose();
    coverMat.dispose();
    paperMat.dispose();
    boxGeo.dispose();
    floorGeo.dispose();
    floorMat.dispose();
    ambientLight.dispose();
    dirLight.dispose();
    fillLight.dispose();
    
    return outCanvas;
  }

  /**
   * Apply a UV transform to a texture so the source image is cover-fit
   * (center-cropped) into the target paper aspect, then zoomed/panned.
   *
   * @param {THREE.Texture} tex
   * @param {number} imageAspect Source image height/width
   * @param {number} targetAspect Target paper height/width
   * @param {object} opts { zoom, panX, panY }
   */
  _applyTextureTransform(tex, imageAspect, targetAspect, opts) {
    const zoom = opts.zoom && opts.zoom > 0 ? opts.zoom : 1;
    let repeatX = 1;
    let repeatY = 1;
    // Cover-fit: crop the overflowing dimension so the image fills the page.
    if (imageAspect > targetAspect) {
      repeatY = targetAspect / imageAspect; // image taller → crop top/bottom
    } else {
      repeatX = imageAspect / targetAspect; // image wider → crop sides
    }
    repeatX /= zoom;
    repeatY /= zoom;

    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.repeat.set(repeatX, repeatY);

    // Pan within the available crop slack (-1..1 maps across the slack range).
    const slackX = 1 - repeatX;
    const slackY = 1 - repeatY;
    const panX = opts.panX ?? 0;
    const panY = opts.panY ?? 0;
    tex.offset.set(
      slackX * (0.5 + 0.5 * panX),
      slackY * (0.5 - 0.5 * panY),
    );
    tex.needsUpdate = true;
  }

  /**
   * Dispose the WebGL renderer and free GPU resources.
   * Call when the renderer is no longer needed (e.g. app teardown).
   */
  dispose() {
    this.renderer.dispose();
  }
}
